import * as React from 'react'
import * as Lodash from 'lodash/fp'

import { Box, Fab, Icon, styled } from '@design/pt-ui'
import { toast } from 'react-toastify'

import SomethingIcon from './SomethingIcon'

import {
  Project,
  ProjectAction,
  ProjectId,
} from '../interfaces/entities/project'
import { User } from '../interfaces/entities/user'
import { Optionset } from '../interfaces/components/options'
import { Descriptive } from '../interfaces/base'
import CreativeSelection from './CreativeSelection'
import ConfirmationDialog from './ConfirmationDialog'
import { Creative } from '../interfaces/entities/creative'
import Contacts from './Contacts'
import EmailDialog from './EmailDialog'
import { IEmail } from '../interfaces/entities/email'
import { translateWithUserLocale } from '../utils/i18n'

const ActionFab = styled(Fab)({
  backgroundColor: '#fff !important',
  fontSize: '11px !important',
  width: '100% !important',
  marginBottom: '5px !important',
  marginTop: '5px !important',
})

interface Props {
  project: Project
  user: User
  clickOnProjectAction: (action: Descriptive, project: Project) => void
  getProject: (id: ProjectId) => void
}

function Header(props: Props) {
  const [state, setState] = React.useState({
    open: false,
    creativeSelectionDialogOpened: false,
    actionConfirmationDialogOpened: false,
    emailDialogOpened: false,
  })

  const getNextAction: (project: Project) => ProjectAction = (project) => {
    const nextAction = project.nextAction
      ? project.nextAction
      : {
          name: 'nop',
          title: 'Nop',
          description: "You shouldn't see  Contact support.",
        }

    return nextAction
  }
  const openActionConfirmationDialog = () => {
    setState((state) => ({
      ...state,
      actionConfirmationDialogOpened: true,
    }))
  }

  const handleDoProjectAction = (action: Descriptive) => {
    if (props.project !== null) {
      props.clickOnProjectAction(action, props.project)
    }
  }

  const openCreativesDialog = () => {
    setState((state) => ({
      ...state,
      creativeSelectionDialogOpened: true,
    }))
  }

  const closeCreativesDialog = () => {
    setState((state) => ({
      ...state,
      creativeSelectionDialogOpened: false,
    }))
  }
  const actionConfirmationDialogConfirm = () => {
    if (props.project) {
      handleDoProjectAction(getNextAction(props.project))
    }
    actionConfirmationDialogClose()
  }

  const actionConfirmationDialogClose = () => {
    setState((state) => ({
      ...state,
      actionConfirmationDialogOpened: false,
    }))
  }

  const handleNextPossibleAction = () => {
    const nextAction = getNextAction(props.project)
    return handlePossibleAction(nextAction)
  }

  const handlePossibleAction = (action: Descriptive) => {
    if (action.name === 'send_creatives') {
      openCreativesDialog()
    } else if (action.name === 'specification_request') {
      openActionConfirmationDialog()
    } else if (action.name !== 'no_actions_required') {
      handleDoProjectAction(action)
    }
  }

  const openEmailDialog = () => {
    setState((state) => ({ ...state, emailDialogOpened: true }))
  }

  const confirmSendToAgency = (response: {
    creatives: Creative[]
    warnings: string[]
  }) => {
    setState((state) => ({
      ...state,
      creativeSelectionDialogOpened: false,
    }))
    if (props.project) {
      props.getProject(props.project.id)
    }

    const message =
      response.warnings.length === 0
        ? translateWithUserLocale(
            'send_creatives_dialog.notification_with_no_warnings',
          )
        : translateWithUserLocale(
            'send_creatives_dialog.notification_with_warnings',
          )

    toast(
      <Contacts
        contacts={
          props.project && props.project.pmContacts
            ? props.project.pmContacts
            : []
        }
        message={message}
      />,
      {
        className: 'toast-dialog',
        autoClose: false,
        position: 'top-center',
      },
    )
  }

  const showEmailDialog = () => {
    return (
      !props.user.groups.includes('client') &&
      [
        'open',
        'work_in_progress',
        'specification_finalized',
        'sent_to_agency',
      ].includes(props.project.status.name)
    )
  }

  const onEmailSent = (email: IEmail) => {
    setState((state) => ({
      ...state,
      emailDialogOpened: false,
    }))
  }

  const closeEmailDialog = () => {
    setState((state) => ({
      ...state,
      emailDialogOpened: false,
    }))
  }

  const nextAction = getNextAction(props.project)
  const actionIcons: Optionset = [
    {
      specification_request: 'lock',
      send_tracking: 'arrow_upward',
      send_creatives: 'send',
      send_to_agency: 'send',
      no_actions_required: 'send',
    },
    'build',
  ]

  const specificationRequest = (
    <div>
      <ConfirmationDialog
        title={translateWithUserLocale(
          'confirmation_dialog.specification_request_title',
        )}
        message={
          <div style={{ display: 'flex' }}>
            <Icon sx={{ marginRight: '5px' }}>info</Icon>
            {translateWithUserLocale(
              'confirmation_dialog.specification_request_message',
            )}
          </div>
        }
        open={state.actionConfirmationDialogOpened}
        onClose={actionConfirmationDialogClose}
        onConfirm={actionConfirmationDialogConfirm}
        no={translateWithUserLocale(
          'confirmation_dialog.specification_request_back',
        )}
        yes={translateWithUserLocale(
          'confirmation_dialog.specification_request_confirm',
        )}
      />
    </div>
  )

  const formatsets = props.project.formatsets
  const sendToAgencyDialog = formatsets ? (
    <CreativeSelection
      dialogOpened={state.creativeSelectionDialogOpened}
      creatives={Lodash.flatten(
        formatsets.map((fs) => {
          return fs.creatives.map((c) => {
            return {
              creative: c,
              formatset: fs,
            }
          })
        }),
      )}
      onActionComplete={confirmSendToAgency}
      onCloseDialog={closeCreativesDialog}
    />
  ) : null

  const emailDialog = (
    <EmailDialog
      project={props.project}
      user={props.user}
      open={state.emailDialogOpened}
      onEmailSent={onEmailSent}
      onCloseDialog={closeEmailDialog}
    />
  )

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100%',
        width: '100%',
      }}>
      <div style={{ width: '100%' }}>
        {nextAction.name === 'no_actions_required' &&
        showEmailDialog() ? null : (
          <>
            {nextAction.name === 'send_creatives' && sendToAgencyDialog}
            {nextAction.name === 'specification_request' &&
              specificationRequest}
            <ActionFab
              variant="extended"
              disabled={nextAction.name === 'no_actions_required'}
              onClick={handleNextPossibleAction}>
              <SomethingIcon
                id={nextAction.name}
                icons={actionIcons}
                fontSize="large"
              />
              <Box component="span" sx={{ marginLeft: '5px' }}>
                {nextAction.title}
              </Box>
            </ActionFab>
          </>
        )}

        {showEmailDialog() ? (
          <>
            {emailDialog}
            <ActionFab variant="extended" onClick={openEmailDialog}>
              <Icon fontSize="large">send</Icon>
              <Box
                component="span"
                sx={{
                  marginLeft: '5px',
                  textAlign: 'left',
                  lineHeight: 2,
                  paddingLeft: 0.5,
                }}>
                {translateWithUserLocale('project.inform_client_button')}
              </Box>
            </ActionFab>
          </>
        ) : null}
      </div>
    </Box>
  )
}

export default Header
